// The composer's "someone got there first" warning (rules: lib/draft-collision.ts).

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from './auth-guard';
import { findCollisions, type Collision } from './draft-collision';
import { getOwnReplyKeys } from './outbox-jobs';
import { useTicketEvents, useTicketMessages } from './queries';

export function useDraftCollision(ticketId: string, hasDraft: boolean) {
  const messagesQ = useTicketMessages(ticketId);
  const eventsQ = useTicketEvents(ticketId);
  const { teamMember } = useAuth();
  const mySlackId = teamMember?.slack_user_id ?? null;
  const messages = messagesQ.data;

  // The baseline: the messages on the ticket when the draft began. Taken once the
  // messages have loaded (a draft restored on page load must not call the whole
  // thread "new"), dropped when the draft is empty or the ticket changes.
  const [baseline, setBaseline] = useState<{ ticketId: string; ids: Set<string> } | null>(null);
  const [acknowledged, setAcknowledged] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!hasDraft) {
      setBaseline(null);
      setAcknowledged(new Set());
      return;
    }
    if (!messages) return;
    setBaseline((prev) => (prev && prev.ticketId === ticketId ? prev : { ticketId, ids: new Set(messages.map((m) => m.id)) }));
  }, [hasDraft, ticketId, messages]);

  const collisions = useMemo<Collision[]>(
    () =>
      findCollisions({
        messages: messages ?? [],
        baseline: baseline && baseline.ticketId === ticketId ? baseline.ids : null,
        events: eventsQ.data ?? [],
        mySlackId,
        mySentKeys: getOwnReplyKeys(),
        acknowledged,
      }),
    [messages, baseline, ticketId, eventsQ.data, mySlackId, acknowledged],
  );

  /** "Got it" / "Send anyway": stop warning about what is there now. */
  const acknowledge = useCallback(() => {
    setAcknowledged((prev) => new Set([...prev, ...collisions.map((c) => c.id)]));
  }, [collisions]);

  return { collisions, acknowledge };
}
